import { useCallback, useEffect, useRef, useState } from 'react';
import { useAnalytics } from '@center-technologies/analytics';
import { useApiClientsOrNull } from '../../../context/ApiClientsContext';
import type { RollupSimulateRequest, RollupSimulateResponse } from '../../../types/statusFactors';

export const ROLLUP_SIMULATION_DEBOUNCE_MS = 400;

/**
 * `idle` — no request is wanted (clean, not an editor, or opened as View).
 * `hidden` — the server refused (403); the preview fails closed and shows nothing, not an error.
 */
export type RollupSimulationState = 'idle' | 'working' | 'result' | 'failed' | 'hidden';

export interface UseRollupSimulationResult {
  state: RollupSimulationState;
  result: RollupSimulateResponse | null;
  retry: () => void;
}

const statusOf = (error: unknown) => (error as { response?: { status?: number } })?.response?.status;

/**
 * Previews an unsaved rollup edit. `candidate` is `null` while there is nothing to preview; every change to it
 * restarts a 400 ms debounce, aborts the request in flight, and a response that is no longer the latest request is discarded.
 */
export function useRollupSimulation(
  sphereId: string | null | undefined,
  factorId: string | null | undefined,
  candidate: RollupSimulateRequest | null
): UseRollupSimulationResult {
  const graphClient = useApiClientsOrNull()?.graphClient;
  const logAnalyticsEvent = useAnalytics();
  const logRef = useRef(logAnalyticsEvent);
  logRef.current = logAnalyticsEvent;
  const [state, setState] = useState<RollupSimulationState>('idle');
  const [result, setResult] = useState<RollupSimulateResponse | null>(null);
  const [retryToken, setRetryToken] = useState(0);
  const sequenceRef = useRef(0);
  const candidateRef = useRef(candidate);
  candidateRef.current = candidate;

  const active = candidate !== null && !!graphClient && !!sphereId && !!factorId;
  const candidateKey = candidate === null ? '' : JSON.stringify(candidate);

  useEffect(() => {
    if (!active || !graphClient) {
      sequenceRef.current += 1;
      setState('idle');
      setResult(null);
      return undefined;
    }
    setState('working');
    const controller = new AbortController();
    const timer = setTimeout(() => {
      const sequence = ++sequenceRef.current;
      graphClient
        .post<RollupSimulateResponse>(`/spheres/${sphereId}/factors/${factorId}/simulate`, candidateRef.current, {
          signal: controller.signal,
          retries: 0,
        })
        .then((response) => {
          if (sequence !== sequenceRef.current) return;
          setResult(response);
          setState('result');
        })
        .catch((error: unknown) => {
          if (sequence !== sequenceRef.current || controller.signal.aborted) return;
          if (statusOf(error) === 403) {
            setResult(null);
            setState('hidden');
            return;
          }
          logRef.current('rollup_preview_failed', undefined, { factorId, httpStatus: statusOf(error) ?? null });
          setState('failed');
        });
    }, ROLLUP_SIMULATION_DEBOUNCE_MS);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [active, graphClient, sphereId, factorId, candidateKey, retryToken]);

  const retry = useCallback(() => setRetryToken((token) => token + 1), []);

  return { state, result, retry };
}
